import {action, computed, observable, observer, useAsObservableSource, useEffect} from '@api'
import {FlexLayoutToolbarMenu, StoresInstance} from '@stores'
import {autorun, trace} from 'mobx'
import {IDisposer} from 'mobx-utils'
import * as React from 'react'
import * as css from './BorderTabSet.css'
import {useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {BorderNode, CLASSES, TabNode, IIcons, showPopup, Orientation, Actions, BorderButton, I18nLabel, DockLocation, ILayoutCallbacks} from '@flexlayout'

/** @hidden @internal */
export interface IBorderTabSetProps {
  border: BorderNode;
  layout: ILayoutCallbacks;
  iconFactory?: (node: TabNode) => React.ReactNode | undefined;
  titleFactory?: (node: TabNode) => React.ReactNode | undefined;
  icons?: IIcons;
}

class BorderTabSetStore extends VipLocalStore {
  constructor(s: StoresInstance, public props: IBorderTabSetProps) {
    super(s)
  }

  @observable ref: HTMLDivElement
  setRef = r => this.ref = r

  onMouseWheel = (e: Event) => {
    e.preventDefault()
  }

  _toDispose: Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f())
    this.ref.removeEventListener('wheel', this.onMouseWheel)
  }

  registerEffects = () => {
    const {_toDispose} = this

    _toDispose.push(
      autorun(() => {
        const {ref} = this
        ref && ref.addEventListener('wheel', this.onMouseWheel, {passive: false})
      }))
  }

  @observable.ref toolbar : HTMLDivElement
  @observable.ref overflowButton : HTMLButtonElement
  @observable.ref stickyButtons: HTMLDivElement
  @observable userControlledLeft = false

  @observable hiddenTabs : Array<{ index: number; node: TabNode }>

  @action onInterceptMouseDown = (event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>) => {
    event.stopPropagation()
  }

  @action onOverflowClick = () => {
    const {overflowButton, hiddenTabs, props: {layout}, onOverflowItemSelect} = this
    showPopup(layout.rootDiv, overflowButton, hiddenTabs, onOverflowItemSelect, layout.getClassName)
  }

  @action onOverflowItemSelect = (item: { node: TabNode; index: number }) => {
    const {props: {layout}} = this
    layout.doAction(Actions.selectTab(item.node.id))
    this.userControlledLeft = false
  }

  @action onFloatTab = () => {
    const {props: {border, layout}} = this
    const selectedTabNode = border.children[border.selected] as TabNode
    if (selectedTabNode !== undefined) {
      layout.doAction(Actions.floatTab(selectedTabNode.id))
    }
  }

  @computed get tabs() {
    const {props: {layout, border, iconFactory, titleFactory, icons}} = this

    return border.children.map((t: TabNode) => <BorderButton
      layout={layout}
      border={border.location.name}
      node={t}
      key={t.id}
      selected={t.isSelected}
      iconFactory={iconFactory}
      titleFactory={titleFactory}
      icons={icons}
    />)
  }
}

/** @hidden @internal */
export const BorderTabSet = observer<IBorderTabSetProps>(function BorderTabSet(props: IBorderTabSetProps) {
  const obsProps = useAsObservableSource(props),
    store = useVipLocalStore((s) => new BorderTabSetStore(s, obsProps)),
    {tabs, stores: {flexLayout: {onContextMenu_forBorderNode}}, onInterceptMouseDown, onFloatTab} = store
  const {border, layout} = props

  useEffect(() => store.registerEffects(), [])

  const toolbarRef = React.useRef<HTMLDivElement | null>(null)
  const overflowbuttonRef = React.useRef<HTMLButtonElement | null>(null)
  const stickyButtonsRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    store.toolbar = toolbarRef?.current
  }, [toolbarRef.current])
  useEffect(() => {
    store.overflowButton = overflowbuttonRef?.current
  }, [overflowbuttonRef.current])
  useEffect(() => {
    store.stickyButtons = stickyButtonsRef?.current
  }, [stickyButtonsRef.current])

  //const {selfRef, position, userControlledLeft, hiddenTabs, onMouseWheel} = useTabOverflow(border, Orientation.flip(border.orientation), toolbarRef, stickyButtonsRef)

  const cm = layout.getClassName

  let style = border.tabHeaderRect?.styleWithPosition({}) ?? {}

  let borderClasses = cm(CLASSES.FLEXLAYOUT__BORDER) + ' ' + cm(CLASSES.FLEXLAYOUT__BORDER_ + border.location.name)
  if (border.className !== undefined) {
    borderClasses += ' ' + border.className
  }

  // allow customization of tabset right/bottom buttons
  let buttons: any[] = []
  const renderState = {headerContent: {}, buttons, stickyButtons: [], headerButtons: []}
  layout.customizeTabSet(border, renderState)
  buttons = renderState.buttons

  let toolbar
  // if (hiddenTabs.length > 0) {
  //   const overflowTitle = layout.i18nName(I18nLabel.Overflow_Menu_Tooltip)
  //   buttons.push(
  //     <button
  //       key='overflowbutton'
  //       ref={overflowbuttonRef}
  //       className={cm('flexlayout__border_toolbar_button_overflow') + ' ' + cm('flexlayout__border_toolbar_button_overflow_' + border.location.name)}
  //       title={overflowTitle}
  //       onClick={onOverflowClick}
  //       onMouseDown={onInterceptMouseDown}
  //       onTouchStart={onInterceptMouseDown}
  //     >
  //       {icons?.more}
  //       {hiddenTabs.length}
  //     </button>
  //   )
  // }

  const selectedIndex = border.selected
  if (selectedIndex !== -1) {
    const selectedTabNode = border.children[selectedIndex] as TabNode
    if (selectedTabNode !== undefined && layout.supportsPopout && selectedTabNode.isEnableFloat && !selectedTabNode.isFloating) {
      const floatTitle = layout.i18nName(I18nLabel.Float_Tab)
      buttons.push(
        <button
          key='float'
          title={floatTitle}
          className={cm(CLASSES.FLEXLAYOUT__BORDER_TOOLBAR_BUTTON) + ' ' + cm(CLASSES.FLEXLAYOUT__BORDER_TOOLBAR_BUTTON_FLOAT)}
          onClick={onFloatTab}
          onMouseDown={onInterceptMouseDown}
          onTouchStart={onInterceptMouseDown}
        />
      )
    }
  }
  toolbar = (
    <div key='toolbar' ref={toolbarRef} className={cm(CLASSES.FLEXLAYOUT__BORDER_TOOLBAR) + ' ' + cm(CLASSES.FLEXLAYOUT__BORDER_TOOLBAR_ + border.location.name)}>
      {buttons}
    </div>
  )

  style = layout.styleFont(style)

  let innerStyle = {}
  const borderHeight = border.borderBarSize - 1
  if (border.location === DockLocation.LEFT) {
    innerStyle = {right: borderHeight, height: borderHeight, top: 0}
  } else if (border.location === DockLocation.RIGHT) {
    innerStyle = {left: borderHeight, height: borderHeight, top: 0}
  } else {
    innerStyle = {height: borderHeight, left: 0}
  }

  return (
    <div ref={store.setRef}
         style={style}
         className={borderClasses}
         data-type='TabSet'
         onContextMenu={onContextMenu_forBorderNode(border)}
         data-id={border.id}
         >
      <div style={{height: borderHeight}} className={cm(CLASSES.FLEXLAYOUT__BORDER_INNER) + ' ' + cm(CLASSES.FLEXLAYOUT__BORDER_INNER_ + border.location.name)}>
        <div style={innerStyle} onDoubleClick={border.onDoubleClick}
             className={cm(CLASSES.FLEXLAYOUT__BORDER_INNER_TAB_CONTAINER) + ' ' + cm(CLASSES.FLEXLAYOUT__BORDER_INNER_TAB_CONTAINER_ + border.location.name)}>
          {false && border.location.name === 'left' && <LeftBorderExtra />}
          {tabs}
        </div>
      </div>
      {toolbar}
    </div>
  )
})

const LeftBorderExtra = observer(function LeftBorderExtra() {
    return (<div className={css.leftExtra}>
      <FlexLayoutToolbarMenu placement='right-start' className={css.layoutMenu}/>
    </div>)
})


// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/BorderTabSet.tsx